import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { LazyMount } from "@/components/LazyMount";
import {
  RelatedPagesGrid,
  type RelatedPageCard,
} from "@/components/RelatedPagesGrid";
import {
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
} from "@/lib/entitySchema";
import { geoImageObject } from "@/lib/geoImageSchema";
import { ogImageFor, ogMetaTags, type OgCityKey, type OgServiceKey } from "@/lib/ogImages";
import { CITY_NAME_TO_SLUG, getCityPage } from "@/lib/citiesData";

const SERVICE_TO_OG: Record<string, OgServiceKey> = {
  seo: "seo",
  "web design": "web-design",
  "google maps": "google-maps",
  ppc: "ppc",
  "social media": "social-media",
  "geo ai": "geo-ai",
};

export type IntersectionFAQ = { q: string; a: string };

/**
 * Tier 7 intersection page config — represents the cross-section of one
 * industry hub × one city hub × one service hub.
 *
 * Per the SEO spec, every Tier 7 page MUST link UP to:
 *   • its industry hub
 *   • its city hub
 *   • its service hub
 *   • its industry spoke (industry × service, statewide)
 */
export type IntersectionPageConfig = {
  /** URL slug WITHOUT leading slash, e.g. "hvac-seo-colorado-springs". */
  slug: string;
  /** Display name of the industry, e.g. "HVAC", "Plumbing". */
  industry: string;
  /** Lowercased industry, e.g. "hvac". */
  industryLower: string;
  /** Display name of the city, e.g. "Colorado Springs". */
  city: string;
  /** Display name of the service, e.g. "SEO", "Web Design". */
  service: string;
  /** Lowercased service phrase, e.g. "seo", "web design". */
  serviceLower: string;
  /** Parent industry hub URL, e.g. "/hvac-marketing-colorado". */
  industryHubTo: string;
  industryHubLabel: string;
  /** Parent city hub URL, e.g. "/digital-marketing". */
  cityHubTo: string;
  cityHubLabel: string;
  /** Parent service hub URL, e.g. "/seo-services". */
  serviceHubTo: string;
  serviceHubLabel: string;
  /** Industry-spoke URL (industry × service, statewide), e.g. "/hvac-seo-colorado". */
  industrySpokeTo: string;
  industrySpokeLabel: string;
  /** Meta title. */
  metaTitle: string;
  /** Meta description. */
  metaDescription: string;
};

function buildIntersectionFaqs(config: IntersectionPageConfig): IntersectionFAQ[] {
  const { industry, city, service, serviceLower } = config;
  return [
    {
      q: `How fast can a ${city} ${industry} company see results from ${service}?`,
      a: `Most ${city} ${industry} companies see meaningful movement in 60–120 days for ${serviceLower}. The Front Range / ${city} market is competitive, so consistent monthly work is what compounds — not one-off projects. Map Pack movement can come faster (30–60 days) if your Google Business Profile is starting from a baseline of basic optimization.`,
    },
    {
      q: `Do you only work with ${industry} companies in ${city}?`,
      a: `No — we serve ${industry} companies across all of Colorado, but ${city} is one of our core markets. Our HQ is in Colorado Springs at 3210 E Woodmen Rd, and we know the ${city} ${serviceLower} landscape intimately because we work in it every day.`,
    },
    {
      q: `What does ${service.toLowerCase()} cost for a ${industry} company in ${city}?`,
      a: `${service} packages for ${industry} companies typically run $1,500–$3,000/month in ${city} depending on scope, competition, and number of service lines. All pricing is published openly on our pricing page — no contracts, month-to-month, with transparent reporting.`,
    },
    {
      q: `Will I work with the owner directly?`,
      a: `Yes — every client works directly with Chris Heidlebaugh, owner of Colorado Web Impressions. No account managers, no offshore teams, no junior staff handoffs. You get Chris's direct line and an average response time under one hour during business hours.`,
    },
  ];
}

export function buildIntersectionHead(config: IntersectionPageConfig) {
  const canonical = `${SITE_URL}/${config.slug}`;
  const faqs = buildIntersectionFaqs(config);

  // City + service lookups for geo-tagged ImageObject schema.
  const citySlug = CITY_NAME_TO_SLUG[config.city];
  const cityPage = citySlug ? getCityPage(citySlug) : undefined;
  const cityZips = cityPage?.zips ?? [];
  const ogServiceKey = SERVICE_TO_OG[config.serviceLower];
  const ogImage = `${SITE_URL}${ogImageFor({
    service: ogServiceKey,
    city: citySlug as OgCityKey | undefined,
  })}`;

  const imageNode = geoImageObject({
    url: ogImage,
    caption: `${config.industry} ${config.service} in ${config.city}, CO${cityZips.slice(0, 2).length ? ` (${cityZips.slice(0, 2).join(", ")})` : ""} — Colorado Web Impressions`,
    description: `${config.industry} ${config.service} for ${config.city}, Colorado${cityZips.length ? ` — serving ZIPs ${cityZips.slice(0, 4).join(", ")}` : ""}. Owner-led local marketing by Colorado Web Impressions.`,
    cityName: config.city,
    zips: cityZips,
    geo: cityPage?.geo,
    width: 1216,
    height: 640,
  });

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: `${config.industry} ${config.service} in ${config.city}`,
        description: config.metaDescription,
        provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
        areaServed: {
          "@type": "City",
          name: config.city,
          containedInPlace: "Colorado",
        },
        url: canonical,
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "1500",
          highPrice: "3000",
        },
      },
      {
        "@type": "LocalBusiness",
        "@id": SCHEMA_LOCALBUSINESS_ID,
        name: "Colorado Web Impressions",
        url: SITE_URL,
        telephone: "+17194193935",
        parentOrganization: { "@id": SCHEMA_ORG_ID },
        founder: { "@id": SCHEMA_PERSON_ID },
        address: {
          "@type": "PostalAddress",
          streetAddress: "3210 E Woodmen Rd STE 110",
          addressLocality: "Colorado Springs",
          addressRegion: "CO",
          postalCode: "80920",
          addressCountry: "US",
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: SITE_URL + "/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: config.industryHubLabel,
            item: `${SITE_URL}${config.industryHubTo}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: config.cityHubLabel,
            item: `${SITE_URL}${config.cityHubTo}`,
          },
          {
            "@type": "ListItem",
            position: 4,
            name: `${config.industry} ${config.service} ${config.city}`,
            item: canonical,
          },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${canonical}#faq`,
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: config.metaTitle,
        primaryImageOfPage: imageNode,
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".speakable-intro", ".faq-answer"],
        },
      },
    ],
  };

  return {
    meta: [
      { title: config.metaTitle },
      { name: "description", content: config.metaDescription },
      { property: "og:title", content: config.metaTitle },
      { property: "og:description", content: config.metaDescription },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      ...ogMetaTags(ogImage),
      { name: "twitter:title", content: config.metaTitle },
      { name: "twitter:description", content: config.metaDescription },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  };
}

export function IntersectionPage({ config }: { config: IntersectionPageConfig }) {
  const faqs = buildIntersectionFaqs(config);

  // Per spec: link UP to industry hub, city hub, service hub, industry spoke.
  const cards: RelatedPageCard[] = [
    {
      to: config.industryHubTo,
      title: config.industryHubLabel,
      description: `Full ${config.industry.toLowerCase()} marketing playbook across Colorado — SEO, Google Maps, web, and ads.`,
    },
    {
      to: config.cityHubTo,
      title: config.cityHubLabel,
      description: `Owner-led digital marketing for ${config.city} businesses — every service we offer, tuned for the local market.`,
    },
    {
      to: config.serviceHubTo,
      title: config.serviceHubLabel,
      description: `How our ${config.serviceLower} engagements work — methodology, pricing, and what to expect.`,
    },
    {
      to: config.industrySpokeTo,
      title: config.industrySpokeLabel,
      description: `Statewide ${config.industry} ${config.serviceLower} — the broader Colorado playbook this ${config.city} page sits inside.`,
    },
  ];

  const heroTitle: ReactNode = (
    <>
      {config.industry} {config.service} in{" "}
      <span className="text-gradient-gold">{config.city}</span>
    </>
  );

  return (
    <SiteLayout>
      <PageHero
        eyebrow={`${config.industry} ${config.service} — ${config.city}`}
        title={heroTitle}
        description={`Local ${config.serviceLower} for ${config.city} ${config.industry.toLowerCase()} companies — owner-led, no contracts, real Colorado results.`}
      >
        <div className="mt-6 flex justify-center">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: config.industryHubLabel, to: config.industryHubTo },
              { label: config.cityHubLabel, to: config.cityHubTo },
              { label: `${config.industry} ${config.service} ${config.city}` },
            ]}
          />
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a free {config.industry} marketing audit
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/5 px-6 py-3 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" />
            (719) 419-3935
          </a>
        </div>
      </PageHero>

      {/* Intro */}
      <section className="bg-background py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="speakable-intro text-lg leading-relaxed text-foreground/85">
            We help {config.city} {config.industry.toLowerCase()} companies turn
            local search into booked jobs. Our {config.serviceLower} work for{" "}
            {config.industry.toLowerCase()} contractors in {config.city} is a focused
            slice of two things we do every day: <a href={config.industryHubTo} className="font-semibold text-brand-gold-deep underline underline-offset-4 hover:text-brand-gold">{config.industryHubLabel}</a>{" "}
            and <a href={config.cityHubTo} className="font-semibold text-brand-gold-deep underline underline-offset-4 hover:text-brand-gold">{config.cityHubLabel}</a>. This page is for owners who want both at once — the
            industry expertise and the local market knowledge.
          </p>

          <div className="mt-8 rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep">
              What is {config.industry} {config.serviceLower} in {config.city}?
            </div>
            <p className="mt-2 text-base leading-relaxed text-foreground/85">
              {config.industry} {config.serviceLower} in {config.city} is the practice of building
              local search visibility for {config.industry.toLowerCase()} contractors serving the {config.city} market —
              combining {config.industry.toLowerCase()}-specific service pages, {config.city}-tuned local SEO signals, and conversion-focused on-page work
              so the calls coming in are from real local customers ready to book.
            </p>
          </div>
        </div>
      </section>

      {/* Why this combination */}
      <section className="border-y border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Why {config.industry} + {config.city} + {config.service} is a
            different problem
          </h2>
          <div className="mt-8 space-y-8">
            <div>
              <h3 className="text-xl font-extrabold text-foreground">
                {config.city} buyer behavior is hyper-local
              </h3>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">
                Generic statewide {config.serviceLower} misses the neighborhood-level signals
                Google now weights heavily in {config.city}. Our work pairs the
                broader Colorado playbook from{" "}
                <a
                  href={config.industrySpokeTo}
                  className="font-semibold text-brand-gold-deep underline underline-offset-4 hover:text-brand-gold"
                >
                  {config.industrySpokeLabel}
                </a>{" "}
                with {config.city}-specific neighborhood, schema, and content tuning.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-foreground">
                {config.industry} demand has a unique seasonal + emergency pattern
              </h3>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">
                {config.industry} contractors live and die by emergency-intent
                searches. We tune {config.serviceLower} for the moment a {config.city} customer
                pulls out their phone with a real problem — not just for vanity rankings.
                See how it fits the broader{" "}
                <a
                  href={config.serviceHubTo}
                  className="font-semibold text-brand-gold-deep underline underline-offset-4 hover:text-brand-gold"
                >
                  {config.serviceHubLabel}
                </a>{" "}
                framework we use across every vertical.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-foreground">
                You're competing against both locals and out-of-state players
              </h3>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">
                {config.city} is a market national franchise brands and out-of-state
                operators target heavily. Local-first {config.serviceLower} —
                grounded in real {config.city} addresses, real Colorado phone
                numbers, and real local content — is how independent {config.industry.toLowerCase()} companies outrank them. That same approach is the foundation of our{" "}
                <a
                  href={config.cityHubTo}
                  className="font-semibold text-brand-gold-deep underline underline-offset-4 hover:text-brand-gold"
                >
                  {config.cityHubLabel}
                </a>{" "}
                work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related grid */}
      <LazyMount minHeight={400} rootMargin="600px 0px">
        <RelatedPagesGrid cards={cards} />
      </LazyMount>

      {/* FAQ */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            FAQ — {config.industry} {config.service} in {config.city}
          </h2>
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card shadow-card"
          >
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="border-0 px-5 sm:px-6"
              >
                <AccordionTrigger className="py-5 text-left text-base font-bold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="faq-answer pb-5 pt-0 text-base leading-relaxed text-foreground/85">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Ready to grow your {config.city} {config.industry.toLowerCase()}{" "}
            company?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Owner-led {config.serviceLower} from our Colorado Springs HQ — no
            contracts, no junior staff, just real Colorado results.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a Free Proposal
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
